// Sprint 8.1 — Catalogue des feature_keys + helper de résolution.
//
// Les feature_keys sont des chaînes "module.action" utilisées dans :
//   - usePermission('caisse.create_op')                    (côté front)
//   - users_metadata.permissions = { 'caisse.create_op': true, ... }  (DB)
//
// Convention :
//   - module.view  : afficher la page / les données
//   - module.edit  : créer / modifier / supprimer
//   - module.X     : action spécifique (ex: caisse.create_op)
//
// Si users_metadata.permissions est null → l'user a TOUT (rétro-compat).
// Si non-null → whitelist stricte (seules les keys true sont autorisées).
// is_admin() bypass toujours.

export type FeatureKey =
  // Caisse
  | 'caisse.view'
  | 'caisse.edit'
  | 'caisse.create_op'
  // Trésorerie / Finances
  | 'tresorerie.view'
  | 'tresorerie.edit'
  | 'justificatifs.view'
  | 'justificatifs.edit'
  | 'cloture.view'
  | 'cloture.edit'
  | 'rapprochement.view'
  | 'rapprochement.edit'
  | 'comptes.view'
  | 'comptes.edit'
  | 'echeanciers.view'
  | 'echeanciers.edit'
  | 'avances.view'
  | 'avances.edit'
  // Modules consolidés
  | 'consolide_formateurs.view'
  | 'consolide_formateurs.edit'
  | 'dettes_fournisseurs.view'
  | 'dettes_fournisseurs.edit'
  | 'recouvrement.view'
  | 'recouvrement.edit'
  // CRM
  | 'crm.view'
  | 'crm.edit'
  // Formation
  | 'formation.view'
  | 'formation.edit'
  // Marketing B2C
  | 'marketing.view'
  | 'marketing.edit'
  // Paramètres
  | 'parametres.view'
  | 'parametres.edit';

export interface FeatureGroup {
  module: string;
  label: string;
  features: { key: FeatureKey; label: string }[];
}

// Catalogue groupé pour l'UI page édition.
export const FEATURE_CATALOG: FeatureGroup[] = [
  {
    module: 'caisse',
    label: 'Caisse',
    features: [
      { key: 'caisse.view', label: 'Voir le module Caisse' },
      { key: 'caisse.create_op', label: 'Créer une opération de caisse' },
      { key: 'caisse.edit', label: 'Modifier / supprimer des opérations' },
    ],
  },
  {
    module: 'finances',
    label: 'Finances — pages spécialisées',
    features: [
      { key: 'tresorerie.view', label: 'Voir Trésorerie' },
      { key: 'tresorerie.edit', label: 'Éditer Trésorerie' },
      { key: 'justificatifs.view', label: 'Voir Justificatifs' },
      { key: 'justificatifs.edit', label: 'Éditer Justificatifs' },
      { key: 'cloture.view', label: 'Voir Clôtures' },
      { key: 'cloture.edit', label: 'Valider / verrouiller des clôtures' },
      { key: 'rapprochement.view', label: 'Voir Rapprochement bancaire' },
      { key: 'rapprochement.edit', label: 'Éditer Rapprochement bancaire' },
      { key: 'comptes.view', label: 'Voir Comptes bancaires' },
      { key: 'comptes.edit', label: 'Éditer Comptes bancaires' },
      { key: 'echeanciers.view', label: 'Voir Échéanciers' },
      { key: 'echeanciers.edit', label: 'Éditer Échéanciers' },
      { key: 'avances.view', label: 'Voir Avances inter-activités' },
      { key: 'avances.edit', label: 'Éditer Avances inter-activités' },
    ],
  },
  {
    module: 'consolide',
    label: 'Modules consolidés',
    features: [
      { key: 'consolide_formateurs.view', label: 'Voir Consolidé Formateurs' },
      { key: 'consolide_formateurs.edit', label: 'Éditer Consolidé Formateurs' },
      { key: 'dettes_fournisseurs.view', label: 'Voir Dettes Fournisseurs' },
      { key: 'dettes_fournisseurs.edit', label: 'Éditer Dettes Fournisseurs' },
      { key: 'recouvrement.view', label: 'Voir Recouvrement Entreprises' },
      { key: 'recouvrement.edit', label: 'Éditer Recouvrement Entreprises' },
    ],
  },
  {
    module: 'crm',
    label: 'CRM',
    features: [
      { key: 'crm.view', label: 'Voir CRM' },
      { key: 'crm.edit', label: 'Éditer CRM (opportunités, pipelines)' },
    ],
  },
  {
    module: 'formation',
    label: 'Formation',
    features: [
      { key: 'formation.view', label: 'Voir Formation' },
      { key: 'formation.edit', label: 'Éditer Formation' },
    ],
  },
  {
    module: 'marketing',
    label: 'Marketing B2C',
    features: [
      { key: 'marketing.view', label: 'Voir Marketing B2C' },
      { key: 'marketing.edit', label: 'Éditer Marketing B2C' },
    ],
  },
  {
    module: 'parametres',
    label: 'Paramètres (admin)',
    features: [
      { key: 'parametres.view', label: 'Voir Paramètres' },
      { key: 'parametres.edit', label: 'Éditer Paramètres' },
    ],
  },
];

// Templates pré-définis pour faciliter la config (Farid, Lynda...).
export interface PermissionsTemplate {
  id: string;
  label: string;
  description: string;
  permissions: Partial<Record<FeatureKey, boolean>>;
}

export const PERMISSIONS_TEMPLATES: PermissionsTemplate[] = [
  {
    id: 'farid_caissier',
    label: '👤 Farid — saisie caisse uniquement',
    description: 'Crée des opérations de caisse, ne voit pas Trésorerie/Justifs/Clôture/etc.',
    permissions: { 'caisse.view': true, 'caisse.create_op': true },
  },
  {
    id: 'lynda_comptable',
    label: '👤 Lynda — comptable transverse',
    description: 'Voir/éditer Consolidé Formateurs, Dettes Fournisseurs, Recouvrement + Caisse.',
    permissions: {
      'caisse.view': true,
      'caisse.edit': true,
      'consolide_formateurs.view': true,
      'consolide_formateurs.edit': true,
      'dettes_fournisseurs.view': true,
      'dettes_fournisseurs.edit': true,
      'recouvrement.view': true,
      'recouvrement.edit': true,
    },
  },
];

// Résout la permission : null = full access (rétro-compat), sinon whitelist.
export function hasPermission(
  permissions: Record<string, boolean> | null | undefined,
  key: FeatureKey,
  isAdmin: boolean,
): boolean {
  if (isAdmin) return true;
  if (permissions == null) return true; // rétrocompat
  return permissions[key] === true;
}
